import type { AxiosInstance } from "axios";
import type {
  PropertyChild,
  PropertyChildInput,
} from "../types/propertyChildren";
import { buildUnitReference } from "../utils/propertyChildren";

type ApiChild = {
  id: number;
  parent_property_id?: number | null;
  apartment_number?: string | number | null;
  unit_reference?: string | null;
  is_rented?: boolean | number | null;
  floor?: string | number | null;
  tower_block?: string | number | null;
  address?: {
    floor?: string | number | null;
    tower_block?: string | number | null;
  } | null;
};

function mapChild(raw: ApiChild): PropertyChild {
  const floor = String(raw.floor ?? raw.address?.floor ?? "").trim();
  const apartmentNumber = String(raw.apartment_number ?? "").trim();
  const towerBlock = String(
    raw.tower_block ?? raw.address?.tower_block ?? ""
  ).trim();

  return {
    id: Number(raw.id),
    parent_property_id:
      raw.parent_property_id != null ? Number(raw.parent_property_id) : null,
    apartment_number: apartmentNumber,
    floor,
    tower_block: towerBlock,
    unit_reference:
      String(raw.unit_reference ?? "").trim() ||
      buildUnitReference(floor, apartmentNumber, towerBlock),
    is_rented: Boolean(raw.is_rented),
  };
}

function extractChildrenList(payload: unknown): ApiChild[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const data = payload as { children?: unknown; data?: unknown };
    if (Array.isArray(data.children)) return data.children;
    if (Array.isArray(data.data)) return data.data as ApiChild[];
  }
  return [];
}

function toApiChildPayload(child: PropertyChildInput) {
  const payload: {
    floor: string;
    apartment_number: string;
    tower_block?: string;
  } = {
    floor: child.floor.trim(),
    apartment_number: child.apartment_number.trim(),
  };

  const tower = child.tower_block?.trim() ?? "";
  if (tower) {
    payload.tower_block = tower;
  }

  return payload;
}

export async function fetchPropertyChildren(
  client: AxiosInstance,
  parentPropertyId: number
): Promise<PropertyChild[]> {
  const response = await client.get(
    `/api/adm/properties/${parentPropertyId}/children`
  );
  return extractChildrenList(response.data?.data ?? response.data).map(mapChild);
}

export async function addPropertyChildren(
  client: AxiosInstance,
  parentPropertyId: number,
  children: PropertyChildInput[]
): Promise<PropertyChild[]> {
  const response = await client.post(
    `/api/adm/properties/${parentPropertyId}/children`,
    {
      children: children.map(toApiChildPayload),
    }
  );

  return extractChildrenList(response.data?.data ?? response.data).map(mapChild);
}

export async function removePropertyChild(
  client: AxiosInstance,
  parentPropertyId: number,
  childId: number
): Promise<void> {
  await client.delete(
    `/api/adm/properties/${parentPropertyId}/children/${childId}`
  );
}

export async function activateProperty(
  client: AxiosInstance,
  propertyId: number
): Promise<void> {
  await client.post(`/api/adm/properties/${propertyId}/activate`);
}

export async function deletePropertyWithChildrenConfirm(
  client: AxiosInstance,
  propertyId: number,
  confirmWithAvailableChildren = false
) {
  return client.delete(`/api/adm/properties/${propertyId}`, {
    data: confirmWithAvailableChildren
      ? { confirm_with_available_children: true }
      : undefined,
  });
}
